import React from 'react';
import type { AttentionItem, GmailMessage } from '../../domain/types';
import { BrandIcon, Badge } from '../../ui/common';
import { ProviderItemDetail } from '../detail/ProviderItemDetail';
import { GmailCardExpanded } from './GmailCardExpanded';
import { buildGmailWebUrl } from './gmailWebUrl';

export interface GmailDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  item: AttentionItem;
  threadMessages: GmailMessage[];
  currentUserEmail?: string;
  linkedTicket?: AttentionItem;
  onNavigateToLinkedTicket?: (id: string) => void;
  onNavigateToJira?: (id: string) => void;
  replyDraft: string;
  onReplyDraftChange: (value: string) => void;
  onSend: (event: React.MouseEvent) => void;
  onGenerateDraft?: (event: React.MouseEvent) => void;
  isSending?: boolean;
  isDrafting?: boolean;
  sendError?: string | null;
  summaryBusy: boolean;
  onGenerateSummary: () => void;
}

/**
 * Complete Gmail thread detail on the shared `ProviderItemDetail` shell
 * (tasks.md §4). Reuses `GmailCardExpanded` (item summary, full thread,
 * message headers, recipients, and the reply composer) instead of
 * duplicating it — the shell only supplies the sticky header/dismissal
 * chrome.
 */
export const GmailDetailModal: React.FC<GmailDetailModalProps> = ({
  isOpen,
  onClose,
  item,
  threadMessages,
  currentUserEmail,
  linkedTicket,
  onNavigateToLinkedTicket,
  onNavigateToJira,
  replyDraft,
  onReplyDraftChange,
  onSend,
  onGenerateDraft,
  isSending,
  isDrafting,
  sendError,
  summaryBusy,
  onGenerateSummary,
}) => {
  if (!isOpen) return null;

  const gmailWebUrl = buildGmailWebUrl(
    threadMessages[0]?.threadId || item.rawGmailMessage?.threadId,
  );

  return (
    <ProviderItemDetail
      isOpen={isOpen}
      onClose={onClose}
      testId="gmail-detail-modal"
      ariaLabel={`Detalle de correo ${item.title}`}
      header={
        <header
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 12,
            padding: '14px 20px',
            borderBottom: '1px solid var(--z-fill-2)',
            background: 'var(--z-surface-subtle)',
            flexWrap: 'wrap',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <BrandIcon source="gmail" size={18} />
            <span
              data-testid="gmail-modal-identity"
              style={{
                fontFamily: "'JetBrains Mono', monospace",
                fontSize: 13,
                fontWeight: 700,
                color: 'var(--z-text)',
              }}
            >
              {item.fromName || item.primary}
            </span>
            {threadMessages.length > 1 ? (
              <Badge label={`${threadMessages.length} mensajes`} tone="flat" />
            ) : null}
            <h1
              data-testid="gmail-modal-title"
              style={{ margin: 0, fontSize: 16, fontWeight: 700, color: 'var(--z-text)' }}
            >
              {item.title}
            </h1>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            {gmailWebUrl ? (
              <a
                href={gmailWebUrl}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="gmail-modal-open-external"
                onClick={(e) => e.stopPropagation()}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                  height: 32,
                  padding: '0 14px',
                  borderRadius: 8,
                  background: 'var(--z-fill)',
                  color: 'var(--z-text-2)',
                  fontSize: 12.5,
                  fontWeight: 600,
                  textDecoration: 'none',
                  cursor: 'pointer',
                }}
              >
                <span>Abrir en Gmail</span>
                <span style={{ fontSize: 11 }}>↗</span>
              </a>
            ) : null}
            <button
              type="button"
              data-testid="gmail-modal-close"
              aria-label="Cerrar modal"
              onClick={onClose}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 32,
                height: 32,
                borderRadius: 8,
                border: 'none',
                background: 'var(--z-fill-2)',
                color: 'var(--z-text-2)',
                cursor: 'pointer',
                fontSize: 14,
              }}
            >
              ✕
            </button>
          </div>
        </header>
      }
    >
      <div style={{ padding: '20px 24px' }}>
        <GmailCardExpanded
          item={item}
          threadMessages={threadMessages}
          currentUserEmail={currentUserEmail}
          linkedTicket={linkedTicket}
          onNavigateToJira={onNavigateToJira ?? onNavigateToLinkedTicket}
          replyDraft={replyDraft}
          onReplyDraftChange={onReplyDraftChange}
          onSend={onSend}
          onGenerateDraft={onGenerateDraft}
          isSending={isSending}
          isDrafting={isDrafting}
          sendError={sendError}
          summaryBusy={summaryBusy}
          onGenerateSummary={onGenerateSummary}
        />
      </div>
    </ProviderItemDetail>
  );
};
